import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Globe, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n, LANG_LABELS } from '../i18n/I18nContext';
import api from '../api/client';
import { LOGO } from '../mock/data';

export default function Login() {
  const { login, register } = useAuth();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin'); // signin | signup | forgot
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  // forgot-password state
  const [resetToken, setResetToken] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [forgotStep, setForgotStep] = useState('email'); // email | reset | done

  const isSignup = mode === 'signup';
  const isForgot = mode === 'forgot';

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    if (isSignup) {
      const res = await register(name, email, password);
      setBusy(false);
      if (res.ok) {
        setError('');
        switchMode('signin');
        setInfo(t('login.accountCreated'));
      } else setError(res.error);
      return;
    }
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate('/');
    else setError(res.error);
  };

  const switchMode = (m) => { setMode(m); setError(''); setInfo(''); setForgotStep('email'); setResetToken(''); setNewPwd(''); };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const data = await api.forgotPassword(email);
      setBusy(false);
      if (data.reset_token) { setResetToken(data.reset_token); setForgotStep('reset'); }
      else setError(t('login.resetTokenHint'));
    } catch (e2) { setBusy(false); setError(t('login.somethingWrong')); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await api.resetPassword(resetToken, newPwd);
      setBusy(false); setForgotStep('done');
    } catch (e2) {
      setBusy(false);
      setError(e2?.response?.data?.detail || t('login.resetFailed'));
    }
  };

  return (
    <div className="kmt-grid-bg min-h-screen w-full flex flex-col items-center justify-center relative px-4 py-10">
      <div className="absolute top-5 right-6 flex items-center gap-1 kmt-panel-2 p-1 rounded-lg">
        <Globe size={15} className="mx-1" style={{ color: 'var(--kmt-muted)' }} />
        {['EN', 'HI', 'PA'].map((l) => (
          <button key={l} onClick={() => setLang(l)} className="px-2.5 py-1 rounded-md text-xs font-mono-k"
            style={{ background: lang === l ? 'var(--kmt-gold)' : 'transparent', color: lang === l ? '#17140a' : 'var(--kmt-muted)', fontWeight: 600 }}>
            {LANG_LABELS[l]}
          </button>
        ))}
      </div>

      <div className="flex flex-col items-center mb-7 kmt-fade-in">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center kmt-panel" style={{ boxShadow: '0 0 0 1px rgba(217,179,74,0.35), 0 0 30px rgba(217,179,74,0.15)' }}>
          <img src={LOGO} alt="KMT" className="w-12 h-12 object-contain" />
        </div>
        <h1 className="font-display text-3xl mt-4" style={{ letterSpacing: '0.35em', color: 'var(--kmt-gold)', paddingLeft: '0.35em' }}>KMT</h1>
        <div className="kmt-label mt-1">MES · APS</div>
      </div>

      <div className="kmt-panel w-full max-w-md p-8 kmt-fade-in">
        {!isForgot && (
          <div className="flex gap-1 p-1 rounded-lg mb-6" style={{ background: '#0e0e10', border: '1px solid var(--kmt-border)' }}>
            {[['signin', t('login.signIn')], ['signup', t('login.signUp')]].map(([m, label]) => (
              <button key={m} onClick={() => switchMode(m)} className="flex-1 py-2 rounded-md text-sm font-medium transition-colors"
                style={{ background: mode === m ? 'var(--kmt-gold)' : 'transparent', color: mode === m ? '#17140a' : 'var(--kmt-muted)' }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {isForgot ? (
          <>
            <button onClick={() => switchMode('signin')} className="flex items-center gap-1.5 text-sm mb-4" style={{ color: 'var(--kmt-muted)' }}>
              <ArrowLeft size={15} /> {t('login.signIn')}
            </button>
            <h2 className="font-display text-xl font-semibold">{t('login.resetTitle')}</h2>
            {forgotStep === 'email' && (
              <form onSubmit={requestReset} className="mt-6 space-y-4">
                <p className="text-sm" style={{ color: 'var(--kmt-muted)' }}>{t('login.resetEmailHint')}</p>
                <div>
                  <label className="kmt-label">{t('login.email')}</label>
                  <input className="kmt-input mt-2" type="email" value={email} placeholder="operator@kmt.com" onChange={(e) => setEmail(e.target.value)} required />
                </div>
                {error && <div className="text-sm" style={{ color: 'var(--kmt-red)' }}>{error}</div>}
                <button type="submit" disabled={busy} className="kmt-btn-gold w-full">{busy ? t('login.sending') : t('login.generateToken')}</button>
              </form>
            )}
            {forgotStep === 'reset' && (
              <form onSubmit={doReset} className="mt-6 space-y-4">
                <div className="kmt-panel-2 p-3 rounded-lg text-xs" style={{ color: 'var(--kmt-muted)' }}>
                  {t('login.noEmailDemo')}
                  <div className="font-mono-k mt-1 break-all" style={{ color: 'var(--kmt-gold)' }}>{resetToken}</div>
                </div>
                <div>
                  <label className="kmt-label">{t('login.newPassword')}</label>
                  <div className="relative mt-2">
                    <input className="kmt-input" style={{ paddingRight: 42 }} type={showPwd ? 'text' : 'password'} value={newPwd} placeholder="••••••••" onChange={(e) => setNewPwd(e.target.value)} required minLength={4} />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--kmt-muted)' }}>{showPwd ? <EyeOff size={17} /> : <Eye size={17} />}</button>
                  </div>
                </div>
                {error && <div className="text-sm" style={{ color: 'var(--kmt-red)' }}>{error}</div>}
                <button type="submit" disabled={busy} className="kmt-btn-gold w-full">{busy ? t('login.resetting') : t('login.setNewPassword')}</button>
              </form>
            )}
            {forgotStep === 'done' && (
              <div className="mt-6 text-center space-y-4">
                <div className="text-sm" style={{ color: 'var(--kmt-green)' }}>{t('login.resetDone')}</div>
                <button onClick={() => switchMode('signin')} className="kmt-btn-gold w-full">{t('login.goToSignIn')}</button>
              </div>
            )}
          </>
        ) : (
          <>
            <h2 className="font-display text-xl font-semibold">{isSignup ? t('login.createAccountTitle') : t('login.title')}</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--kmt-muted)' }}>{isSignup ? t('login.registerSub') : t('login.subtitle')}</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {isSignup && (
                <div>
                  <label className="kmt-label">{t('login.fullName')}</label>
                  <input className="kmt-input mt-2" type="text" value={name} placeholder="Jane Doe" onChange={(e) => setName(e.target.value)} required />
                </div>
              )}
              <div>
                <label className="kmt-label">{t('login.email')}</label>
                <input className="kmt-input mt-2" type="email" value={email} placeholder="you@company.com" onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="kmt-label">{t('login.password')}</label>
                  {!isSignup && <button type="button" onClick={() => switchMode('forgot')} className="text-xs" style={{ color: 'var(--kmt-gold)' }}>{t('login.forgot')}</button>}
                </div>
                <div className="relative mt-2">
                  <input className="kmt-input" style={{ paddingRight: 42 }} type={showPwd ? 'text' : 'password'} value={password} placeholder="••••••••" onChange={(e) => setPassword(e.target.value)} required minLength={4} data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--kmt-muted)' }}>{showPwd ? <EyeOff size={17} /> : <Eye size={17} />}</button>
                </div>
              </div>
              {isSignup && (
                <div className="text-xs rounded-lg p-3" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid var(--kmt-gold-dim)', color: 'var(--kmt-muted)' }} data-testid="signup-pending-note">
                  {t('login.pendingNote')}
                </div>
              )}
              {info && <div className="text-sm" style={{ color: 'var(--kmt-gold)' }} data-testid="login-info">{info}</div>}
              {error && <div className="text-sm" style={{ color: 'var(--kmt-red)' }} data-testid="login-error">{error}</div>}
              <button type="submit" disabled={busy} className="kmt-btn-gold w-full mt-2" data-testid="login-submit-btn">
                {busy ? (isSignup ? t('login.creating') : t('login.signingIn')) : (isSignup ? t('login.createAccountBtn') : t('login.signIn').toUpperCase())}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div style={{ height: 1, flex: 1, background: 'var(--kmt-border)' }} />
              <span className="kmt-label" style={{ fontSize: 10 }}>{t('login.or')}</span>
              <div style={{ height: 1, flex: 1, background: 'var(--kmt-border)' }} />
            </div>
            <button type="button" onClick={googleLogin} data-testid="google-login-btn"
              className="w-full flex items-center justify-center gap-3 py-2.5 rounded-lg font-medium"
              style={{ background: '#fff', color: '#1f1f1f' }}>
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" width="18" height="18" />
              {t('login.continueGoogle')}
            </button>

            {isSignup ? (
              <div className="mt-5 text-center text-sm" style={{ color: 'var(--kmt-muted)' }}>
                {t('login.alreadyHave')} <button onClick={() => switchMode('signin')} style={{ color: 'var(--kmt-gold)', fontWeight: 600 }}>{t('login.signIn')}</button>
              </div>
            ) : (
              <div className="mt-5 text-center text-sm" style={{ color: 'var(--kmt-muted)' }}>
                {t('login.newUser')} <button onClick={() => switchMode('signup')} style={{ color: 'var(--kmt-gold)', fontWeight: 600 }}>{t('login.createAccount')}</button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="kmt-label mt-8 text-center">{t('login.footer')}</div>
    </div>
  );
}
